import { useState, useEffect, useCallback } from 'preact/hooks'
import { hubspotApi } from '@/api/hubspot'
import { apiClient } from '@/api/client'
import type { HubSpotConnectionStatus } from '@/types/hubspot'
import { httpsUrlOrThrow } from '@/utils/externalUrl'

export function useHubSpotAuth(clientCompanyId: string, enabled = true) {
    const [status, setStatus] = useState<HubSpotConnectionStatus | null>(null)
    const [isLoading, setIsLoading] = useState(enabled && !!clientCompanyId)
    const [isConnecting, setIsConnecting] = useState(false)

    const checkStatus = useCallback(async () => {
        if (!clientCompanyId) {
            setIsLoading(false)
            return
        }
        setIsLoading(true)
        try {
            const response = await hubspotApi.getStatus(clientCompanyId)
            if (response.data) {
                setStatus(response.data)
            } else {
                setStatus({ isConnected: false })
            }
        } catch {
            setStatus({ isConnected: false })
        } finally {
            setIsLoading(false)
        }
    }, [clientCompanyId])

    useEffect(() => {
        if (enabled) {
            checkStatus()
        } else {
            // Panel closed: drop the stale status and arm loading so the next open paints
            // the spinner immediately, instead of briefly rendering last session's status
            // before the refetch resolves.
            setStatus(null)
            setIsLoading(true)
        }
    }, [enabled, checkStatus])

    // Initiate OAuth flow in popup
    const connect = useCallback(async () => {
        setIsConnecting(true)
        try {
            const response = await hubspotApi.initiate(clientCompanyId)
            if (!response.data?.authorizationUrl) {
                throw new Error('Failed to get authorization URL')
            }

            // Open popup
            const width = 600
            const height = 800
            const left = window.screenX + (window.outerWidth - width) / 2
            const top = window.screenY + (window.outerHeight - height) / 2
            const popup = window.open(
                httpsUrlOrThrow(response.data.authorizationUrl),
                'hubspot-oauth',
                `width=${width},height=${height},left=${left},top=${top},popup=yes`
            )

            if (!popup) {
                setIsConnecting(false)
                return
            }

            // Poll for popup close
            const pollTimer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    setIsConnecting(false)
                    checkStatus()
                }
            }, 500)

            // Listen for postMessage from callback page
            const apiOrigin = new URL(apiClient.getBaseUrl()).origin
            const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin && event.origin !== apiOrigin) return
                if (event.source !== popup) return
                if (event.data?.type === 'hubspot-oauth-success') {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    popup.close()
                    setIsConnecting(false)
                    checkStatus()
                }
                if (event.data?.type === 'hubspot-oauth-error') {
                    clearInterval(pollTimer)
                    window.removeEventListener('message', handleMessage)
                    popup.close()
                    setIsConnecting(false)
                }
            }
            window.addEventListener('message', handleMessage)
        } catch {
            setIsConnecting(false)
        }
    }, [clientCompanyId, checkStatus])

    const disconnect = useCallback(async () => {
        try {
            await hubspotApi.disconnect(clientCompanyId)
            setStatus({ isConnected: false })
        } catch {
            // Disconnect failed — status unchanged
        }
    }, [clientCompanyId])

    return {
        isConnected: status?.isConnected ?? false,
        connectionId: status?.id,
        isConfigured: !!status?.configuredAtUtc,
        configuredAtUtc: status?.configuredAtUtc ?? null,
        marketingEventsAvailable: status?.marketingEventsAvailable ?? false,
        scopes: status?.scopes ?? '',
        status: status?.status ?? '',
        // Health parity fields (surfaced for the summary's status badge + error detail).
        isSoftFailure: status?.isSoftFailure ?? false,
        consecutiveFailureCount: status?.consecutiveFailureCount ?? 0,
        recentErrors: status?.recentErrors ?? [],
        aiSummary: status?.aiSummary ?? null,
        // Summary readout fields.
        hubId: status?.hubId ?? null,
        connectedAtUtc: status?.connectedAtUtc ?? null,
        lastOutboundSyncUtc: status?.lastOutboundSyncUtc ?? null,
        lastInboundSyncUtc: status?.lastInboundSyncUtc ?? null,
        isLoading,
        isConnecting,
        connect,
        disconnect,
        refreshStatus: checkStatus,
    }
}
